/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Listables Thunk
 */

import { dedupeByUid, getListablesApiParams } from '@zcode-ui/filters'
import { getFeaturedAndSpotlightLimits, selectIsOnline } from '@zcode-ui/web-zumper-global-state'
import {
  LUXURY_RE,
  ON_SERVER,
  definitelyError,
  getValidationErrors,
  isCommonAuthError,
} from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import { LISTABLES_API_LIMIT, LISTABLES_API_LIMIT_MAX } from '@zcode/nr/es/shared/constants/list'
import { ON_BROWSER } from '@zcode/nr/es/shared/env'
import { warnOffline } from '@zcode/nr/es/shared/modules/app/thunks'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware'
import { requeue } from '@zcode/nr/es/shared/packages/offline-middleware/actions'
import { calculateBoxForListing, chomp } from '@zcode/nr/es/shared/utils'
import { isBoxSearch } from '@zcode/nr/es/shared/utils/location'
import type {
  CardBackResponse,
  ExtraThunkArg,
  Listable as ListableType,
  Listables,
  Location,
  SaveSearchLocationBox,
} from '@zcode/state-type'
import { ONE_SECOND } from '@zcode/utils'
import { currentSearchRoutes } from '@zcode/zumper-routes'
import { createAction } from 'redux-actions'
import { hasLatAndLng } from '../components/main/Rail/CurrentSearchView/helpers/hasLatAndLng'
import { transformLowResultsListablesResponse } from '../components/main/Rail/CurrentSearchView/helpers/transformLowResultsListablesResponse'
import {
  checkListablesFetching,
  getCurrentLocation,
  getCurrentSearchLimit,
  getCurrentSearchListables,
  getCurrentSearchListablesQuery,
  getCurrentSearchMatching,
  getCurrentSearchOffset,
  getHasCurrentSearchListables,
  getHasNearbyListables,
  getLoadMoreCount,
} from '../inspectors/index'
import { logger } from '../logger'
import {
  hasMoreListables,
  noMoreListables,
  setFirstPageAdditionalCount,
} from '../modules/currentSearch/actions/index'
import {
  selectFirstPageAdditionalCount,
  selectHasMoreListables,
  selectListables,
} from '../modules/currentSearch/selectors/index'
import { selectFilters } from '../modules/filters/selectors/index'
import type { Dispatch, GetState } from '../modules/types'
import * as ActionC from './constants'
import { appendFeaturedData } from './helpers/appendFeaturedData'
import { appendListableData } from './helpers/appendListableData'
import { appendSpotlightData } from './helpers/appendSpotlightData'
import { getListableCardbackQuery } from './helpers/getListableCardbackQuery'
import { getRawListableCardbackQuery } from './helpers/getRawListableCardbackQuery'

const MIN_CITIES_LIMIT = 50
export const NEARBY_THRESHOLD = 25 // NOTE: this is a less-than (not less than or equal)
export const EXPANDED_RESULTS_LIMIT = 25
const NEARBY_CITIES_MILES = 75

export const getMinCitiesApiParams = (location: Location) => {
  if (isBoxSearch(location)) {
    return {
      ...location.box,
      limit: MIN_CITIES_LIMIT,
    }
  }
  return {
    urls: location.url == null ? [] : [location.url],
    limit: MIN_CITIES_LIMIT,
  }
}

export const getNearbyCitiesApiParams = (
  location: Location & { readonly lat: number; readonly lng: number }
) => {
  const { lat, lng } = location
  const box = calculateBoxForListing({
    lat: chomp(lat, 2),
    lng: chomp(lng, 2),
    miles: NEARBY_CITIES_MILES,
  })
  return {
    ...box,
    limit: MIN_CITIES_LIMIT,
  }
}

export const getMinCities =
  ({
    query,
    zoom,
  }: {
    query: { limit: number } & (SaveSearchLocationBox | { urls: string[] })
    zoom?: number | null
  }) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const isOnline = selectIsOnline(state)
    if (!isOnline) {
      dispatch(warnOffline())
      return
    }
    dispatch({ type: ActionC.GET_MCITIES_REQUEST })
    try {
      const minCities = await Api.mcities.post(query)
      const filteredCities = minCities.filter((city) => city.listingCount)
      dispatch({
        type: ActionC.GET_MCITIES_SUCCESS,
        minCities: filteredCities,
        zoom,
      })
      return filteredCities
    } catch (error) {
      dispatch({ type: ActionC.GET_MCITIES_ERROR, error })
      if (!isCommonAuthError(error)) {
        logger.error('Failed to get minCities', (error as any).data || error)
      }
      throw Error(error as string)
    }
  }

export const resetMinCities = createAction(ActionC.RESET_MCITIES)

export const startFetchingListables = createAction(ActionC.GET_LISTABLES_REQUEST)

export interface GetListablesProps {
  isBot?: boolean
  isCurrentSearchView?: boolean
  isShortTerm?: boolean
  loadNextListables?: boolean
  page?: number
  query: { [key: string]: any }
  spotlightQuery?: { [key: string]: any }
}

// TODO move to a thunk when currentSearch gets its own module
export const getListables =
  ({
    isBot = false,
    isCurrentSearchView,
    isShortTerm,
    loadNextListables,
    page,
    query,
    spotlightQuery,
  }: GetListablesProps) =>
  async (dispatch: Dispatch, getState: GetState, { optimizely }: ExtraThunkArg) => {
    const state = getState()
    const isOnline = selectIsOnline(state)
    const firstPageAdditionalCount = selectFirstPageAdditionalCount(state)
    const { DEFAULT_FEATURED_API_LIMIT, DEFAULT_FEATURED_LIMIT, DEFAULT_SPOTLIGHT_LIMIT } =
      getFeaturedAndSpotlightLimits(optimizely)
    if (!isOnline) {
      dispatch(warnOffline())
      return await Promise.reject(Error('connection error: getListables'))
    }

    // TODO(grady,yewon): move the responsibility for computing the correct featuredLimit to code
    // that calls this code, this should NOT change a given query imo.
    //
    // NOTE: we can infer that we want to append the results as the "next page"
    // when we have an excludeGroupIds in the query. Otherwise we can assume that this is
    // a fresh query.
    //
    // We want to force clear the featuredLimit if one is provided while paginating or getting byIds
    const { byIds, excludeGroupIds, offset } = query
    const queryCopy: { [key: string]: any } = { ...query }
    if (excludeGroupIds || offset || byIds) {
      delete queryCopy.featuredLimit
      delete queryCopy.nearby
    }
    queryCopy.descriptionLength = 580
    if (ON_BROWSER) {
      dispatch(startFetchingListables())
    }

    let listablesPromises:
      | [
          listables: Promise<Listables>,
          featured: ReturnType<(typeof Api)['mapListFeatured']['query']>,
          spotlight?: ReturnType<(typeof Api)['maplistSpotlight']['query']>,
        ]
      | [
          listables: Promise<Listables>,
          featured?: ReturnType<(typeof Api)['featured']['query']>,
          spotlight?: never,
        ] = isShortTerm
      ? [Api.strListables.query(queryCopy)]
      : [isCurrentSearchView ? Api.mapListSearch.query(queryCopy) : Api.listables.query(queryCopy)]

    const routeProps = dispatch(getRouteProps({ routes: currentSearchRoutes }))

    if (page === 1 && !loadNextListables) {
      dispatch(resetListables())
    }

    if (
      page === 1 &&
      LUXURY_RE.exec(routeProps?.location?.pathname) === null &&
      !isShortTerm &&
      !loadNextListables && // do not re-fetch featured when loading next listables
      !isBot
    ) {
      let sort = queryCopy.sort?.filter((s: string) => s !== 'impressions')
      if (sort?.length === 0) {
        sort = undefined
      }
      if (isCurrentSearchView) {
        listablesPromises = [
          listablesPromises[0],
          Api.mapListFeatured.query({
            filters: { ...queryCopy, limit: DEFAULT_FEATURED_API_LIMIT },
            randomize: true,
          }),
        ]
        if (spotlightQuery?.url) {
          listablesPromises.push(
            Api.maplistSpotlight.query({
              filters: { ...spotlightQuery, limit: DEFAULT_SPOTLIGHT_LIMIT },
            })
          )
        }
      } else {
        listablesPromises.push(
          Api.featured.query({ ...queryCopy, limit: DEFAULT_FEATURED_LIMIT, randomize: true, sort })
        )
      }
    }

    try {
      const seenUids = new Set<ListableType['uId']>()
      let [listables, featuredArray, spotlightArray] = await Promise.all(listablesPromises)

      let listablesCardbacks: CardBackResponse = {}
      let featuredCardbacks: CardBackResponse = {}
      let spotlightCardBacks: CardBackResponse = {}

      // Get additional data for back of list cards if on server and isBot
      if (ON_SERVER && isBot) {
        const listablesCardbackQuery = getListableCardbackQuery(listables.listables)
        const featuredCardbackQuery =
          !Array.isArray(featuredArray) && featuredArray?.listables.length
            ? getRawListableCardbackQuery(featuredArray.listables)
            : {}
        const spotlightCardbackQuery = getRawListableCardbackQuery(spotlightArray?.listables)

        try {
          ;[listablesCardbacks, featuredCardbacks, spotlightCardBacks] = await Promise.all([
            Api.cardBack.query(listablesCardbackQuery),
            Api.cardBack.query(featuredCardbackQuery),
            Api.cardBack.query(spotlightCardbackQuery),
          ])
        } catch (e) {
          logger.error('Error loading back of card data', e)
        }
      }

      // Append cardback and other data for listables
      appendListableData(listables, listablesCardbacks)
      const spotlight = appendSpotlightData({ spotlightArray, seenUids, spotlightCardBacks }) ?? []
      const featured =
        appendFeaturedData({
          featuredArray,
          seenUids,
          featuredCardbacks,
          featuredApiLimit: DEFAULT_FEATURED_API_LIMIT,
          spotlightLimit: DEFAULT_SPOTLIGHT_LIMIT,
        }) ?? []

      if (featured.length) {
        listables = { ...listables, featured }
      }
      if (spotlight.length) {
        listables = { ...listables, spotlight }
      }
      if (page !== 1) {
        listables = { ...listables, featured: [], spotlight: [] }
      }
      if (ON_BROWSER) {
        logger.debug('got listables', queryCopy, listables)
      }
      // for the load more listings button to see that there are more listings to load
      listables.listables.length >= queryCopy.limit
        ? dispatch(hasMoreListables())
        : dispatch(noMoreListables())

      listables = { ...listables, query: queryCopy }
      let dedupedListables = dedupeByUid(listables.listables, spotlight, featured)

      // when loading the next listables, we're not re-fetching "featured" or "nearby" so we have to set them here from the SSR listables
      if (loadNextListables) {
        const ssrListables = selectListables(getState())
        dedupedListables = dedupeByUid(
          ssrListables.listables.concat(listables.listables),
          spotlight,
          featured
        )

        listables = {
          ...listables,
          featured: ssrListables.featured,
          isLowResults: ssrListables.isLowResults,
          listables: ssrListables.listables.concat(listables.listables),
          listingCount: ssrListables.listingCount,
          matching:
            ssrListables.matching -
            ssrListables.listables.concat(listables.listables).length +
            dedupedListables.length,
          nearby: ssrListables.nearby,
          spotlight: ssrListables.spotlight,
        }
      } else if (!isShortTerm) {
        const additionalCount =
          page !== 1
            ? firstPageAdditionalCount
            : dedupedListables.length - listables.listables.length
        listables = transformLowResultsListablesResponse(
          listables,
          dedupedListables,
          additionalCount,
          queryCopy.sorts
        )
      }

      if (page === 1) {
        const additionalListableCount =
          dedupedListables.length - listables.listables.length > 0
            ? dedupedListables.length - listables.listables.length
            : 0

        dispatch(setFirstPageAdditionalCount(additionalListableCount))
      }

      // TODO: use an action creator
      dispatch({
        type: ActionC.GET_LISTABLES_SUCCESS,
        listables,
        excludeGroupIds,
        offset,
      })

      // Ensure we have usable results; check for listables in redux
      const state = getState()
      const hasListables = getHasCurrentSearchListables(state)
      const hasNearbyListables = getHasNearbyListables(state)
      const matching = getCurrentSearchMatching(state)

      // We don't have enough listables, not even nearby listables; we should load nearby cities.
      if ((!hasListables || matching < NEARBY_THRESHOLD) && !hasNearbyListables) {
        const location = getCurrentLocation(state)
        // sometimes we don't have a valid location (like on the server)
        if (hasLatAndLng(location)) {
          const nearbyCitiesQuery = getNearbyCitiesApiParams(location)
          // NOTE: we want join the promise chain here and ensure that we are
          // still returning the listables query.
          return await dispatch(getMinCities({ query: nearbyCitiesQuery })).then(() => listables)
        }
      }
      return listables
    } catch (error) {
      dispatch(noMoreListables())
      const validationErrors = getValidationErrors(error)
      let forwardError = error
      if (validationErrors) {
        forwardError = definitelyError(`validation error for needed listables: ${validationErrors}`)
        logger.warn('error for needed listables', query, forwardError)
      } else {
        logger.warn(
          'error for needed listables',
          query,
          definitelyError('error for needed listables', error)
        )
      }
      dispatch({ type: ActionC.GET_LISTABLES_ERROR, error })
      return await Promise.reject(forwardError)
    }
  }

export const setListablesLimit = (limit: number = LISTABLES_API_LIMIT) => ({
  type: ActionC.SET_LISTABLES_LIMIT,
  payload: limit,
})

export const setListablesOffset = (offset: number) => ({
  type: ActionC.SET_LISTABLES_OFFSET,
  payload: offset,
})

export const setListablesPageOffset = (offset: number) => ({
  type: ActionC.SET_LISTABLES_PAGE_OFFSET,
  payload: offset,
})

export const incrementLoadMoreCount = () => ({
  type: ActionC.INCREMENT_LOAD_MORE_COUNT,
})

export const resetListables = () => ({
  type: ActionC.RESET_LISTABLES,
})

// TODO move to a thunk when currentSearch gets its own module
export const loadMoreListables = () => async (dispatch: Dispatch, getState: GetState) => {
  const state = getState()
  const isOnline = selectIsOnline(state)

  if (!isOnline) {
    const warningMessage =
      'Seems that you have been disconnected. We will try to load more listings once you reconnect.'
    dispatch(warnOffline({ message: warningMessage }))
    const callback = async () => await dispatch(loadMoreListables())
    const options = { before: ONE_SECOND * 30 }
    const name = 'loadMoreListables'
    dispatch(requeue({ callback, options, name }))
    return await Promise.reject(Error('connection error: loadMoreListables'))
  }

  // first, gather the data we need to make this call
  // NOTE: hasMore here behaves differently than other places since loadMoreListings
  // will only be triggered when clicking the button. This value is set in state
  // when calling getListables, and will be false when the response's listables length is
  // less than the limit param, indicating that there are no more to load.
  const hasMore = selectHasMoreListables(state)
  const routeProps = dispatch(getRouteProps({ routes: currentSearchRoutes }))
  const loading = checkListablesFetching(state, routeProps)

  const currentLimit = getCurrentSearchLimit(state)
  // NOTE: we need to bump the offset by the currentLimit so that we grab
  // the results continuing from the last query.
  const offset = getCurrentSearchOffset(state) + currentLimit

  // Update the offset in redux
  dispatch(setListablesOffset(offset))

  // bail if we're already loading or there's nothing more to load
  if (loading || !hasMore) {
    // TODO: we would prefer to simply `return`. We're only returning an empty
    // promise to support cases like BubbleDetail that expect thunks to
    // return promises.
    return await Promise.resolve()
  }
  // construct the query from the existing state
  const location = getCurrentLocation(state)
  const filters = selectFilters(state)
  const currentListables = getCurrentSearchListables(state)
  const groupIds = currentListables.map((listable) => listable.groupId)
  // NOTE: we need to ensure that our offset query uses the same location as the
  // original. On cold load this typically means we're using location.url
  // instead of location.box. Thankfully, we're storing the query used to fetch
  // the current listables. If the previous query wasn't using a box, then the
  // offset query shouldn't either.
  const previousQuery = getCurrentSearchListablesQuery(state)
  const noBox = !!previousQuery?.url || !!previousQuery?.get?.('url')
  const params = getListablesApiParams({
    location: noBox ? { url: location.url } : location,
    filters,
    matching: false,
    jsFilters: {
      excludeGroupIds: groupIds,
    },
  })

  // NOTE: we keep increasing the limit so that users don't have to keep
  // pressing the load more button to view more listings. Don't fetch more
  // than 100 (LISTABLES_API_LIMIT_MAX) listings at once to avoid
  // overloading the API server
  const count = getLoadMoreCount(state)
  const limit =
    currentLimit === LISTABLES_API_LIMIT_MAX
      ? currentLimit
      : Math.min((count + 1) * LISTABLES_API_LIMIT, LISTABLES_API_LIMIT_MAX)

  // Update the limit in redux if it has changed
  if (limit !== currentLimit) {
    dispatch(setListablesLimit(limit))
  }

  // increment the loadMore count
  dispatch(incrementLoadMoreCount())

  // we force these params because getListablesApiParams doens't add them correctly
  params.limit = limit

  // TODO: it is bad practice to return a promise from a thunk as it encourages
  // antipatterns. If you need to know when the listables are done loading, you
  // can use the checkListablesFetching inspector or handle the
  // GET_LISTABLES_SUCCESS action in your reducer
  return await dispatch(getListables({ query: params }))
}
